import React, {useState, useEffect, useRef, useCallback} from 'react'
import {GoogleMap, useJsApiLoader} from '@react-google-maps/api'
import {MarkerClusterer} from '@googlemaps/markerclusterer'
import {MINZOOM} from 'helpers/locations'
import {getAlphaLabel} from 'helpers/labels'
import {createMapIcon} from 'helpers/images'

const mapContainerStyle = {
  width: '100%',
  height: '100%'
}

const findLocationByPosition = (locations, position) => {
  const threshold = 0.000001
  return locations.find(location => {
    const latitude = parseFloat(location.latitude?.toString().trim() ?? '')
    const longitude = parseFloat(location.longitude?.toString().trim() ?? '')

    if (isNaN(latitude) || isNaN(longitude)) return false

    return Math.abs(latitude - position.lat()) < threshold && Math.abs(longitude - position.lng()) < threshold
  })
}

const LocationsMap = ({locations, onLocationClick = () => {}, minZoom = MINZOOM.TENANTS}) => {
  const [map, setMap] = useState(null)
  const clustererRef = useRef(null)

  const {isLoaded} = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY
  })

  const onLoad = useCallback(mapInstance => {
    setMap(mapInstance)
  }, [])

  useEffect(() => {
    if (!map || !locations?.length) return

    if (clustererRef.current) {
      clustererRef.current.clearMarkers()
    }

    const bounds = new window.google.maps.LatLngBounds()

    const markers = locations.map((location, index) => {
      const position = {
        lat: parseFloat(location.latitude),
        lng: parseFloat(location.longitude)
      }

      bounds.extend(position)

      const marker = new window.google.maps.Marker({
        position,
        title: location.name,
        icon: createMapIcon(location.key),
        clickable: !!location.id,
        draggable: false
      })
      marker.addListener('click', event => {
        const loc = findLocationByPosition(locations, event.latLng)
        if (loc) {
          onLocationClick(loc)
        }
      })

      return marker
    })

    // Create a new clusterer with gradient colored border markers
    // prettier-ignore
    clustererRef.current = new MarkerClusterer({
      map,
      markers,
      renderer: {
        render: ({ count, position }) => {
          const size = 30
          const gradientColor = `rgb(${Math.min(255, count * 30)}, ${Math.min(255, 255 - count * 20)}, 100)`
const svg = `
  <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
  <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2}" fill="#cce0ff" />
  <circle cx="${size / 2}" cy="${size / 2}" r="${(size / 2)*.85}" fill="#99c2ff"  />
  <circle cx="${size / 2}" cy="${size / 2}" r="${(size / 2) * .70 }" fill="#65aaff"/>
  <circle cx="${size / 2}" cy="${size / 2}" r="${(size / 2) * .55}" fill="#4285F4"/>
  <text x="50%" y="50%" text-anchor="middle" dominant-baseline="middle" fill="white" font-size="12" font-weight="bold" font-family="Arial, sans-serif">${count}</text>
  </svg>
`


          return new window.google.maps.Marker({
            position,
            icon: {
              url: `data:image/svg+xml;base64,${btoa(svg)}`,
              scaledSize: new window.google.maps.Size(size, size)
            },
            zIndex: Number(window.google.maps.Marker.MAX_ZINDEX) + count
          })
        }
      }
    })

    map.fitBounds(bounds)

    setTimeout(() => {
      const currentZoom = map.getZoom()
      if (currentZoom > minZoom) {
        map.setZoom(minZoom)
      }
    }, 100)

    return () => {
      clustererRef.current?.clearMarkers()
    }
  }, [map, locations, onLocationClick, minZoom])

  const mapClicked = event => {
    console.log('Map clicked at:', event.latLng.lat(), event.latLng.lng())
  }

  if (!isLoaded) return <div>Loading map...</div>

  return (
    <GoogleMap
      id="locations-map"
      mapContainerStyle={mapContainerStyle}
      onLoad={onLoad}
      onClick={mapClicked}
      options={{
        mapTypeId: 'roadmap',
        controlSize: 18,
        zoomControl: true,
        mapTypeControl: true,
        scaleControl: false,
        streetViewControl: true,
        rotateControl: false,
        fullscreenControl: true
      }}
    />
  )
}

export default LocationsMap
